import { formatDistanceToNowStrict } from "date-fns";
import { Notebook } from "lucide-react";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import { AvatarStack } from "~/pages/manifold-nextgen/components/AvatarStack";
import { EditableDescription } from "~/pages/manifold-nextgen/components/EditableDescription";
import { EditableTitle } from "~/pages/manifold-nextgen/components/EditableTitle";
import {
  EntityOverviewHeader,
  OverviewTile,
} from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { useOwners } from "~/pages/manifold-nextgen/hooks/useOwners";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { ShareLevel, projectListPath, projectPath, updateProject } from "~/services/projects";
import { ACCESS_LEVEL_LABEL } from "./ProjectAccessBadge";
import { useProjectContext } from "./useProjectContext";

export const ProjectOverview = () => {
  const { projectId, project, attachedDatasets, attachedAgents, isOwner } = useProjectContext();
  const { collaborators, accessLevels } = useProjectShare(projectId);
  const { showSuccess, showError } = useToast();

  const handleRename = async (next: string) => {
    try {
      await updateProject(projectId, { name: next });
      await Promise.all([mutate(projectPath(projectId)), mutate(projectListPath())]);
      showSuccess("Project renamed");
    } catch (e) {
      showError("Failed to rename project");
      throw e;
    }
  };

  const handleSaveDescription = async (next: string) => {
    try {
      await updateProject(projectId, { description: next });
      await mutate(projectPath(projectId));
      showSuccess("Description updated");
    } catch (e) {
      showError("Failed to update description");
      throw e;
    }
  };

  const owners = useOwners(collaborators, accessLevels, ShareLevel.OWNER);

  const tiles: OverviewTile[] = [
    {
      label: "My Role",
      content: project.access_level ? ACCESS_LEVEL_LABEL[project.access_level] : "—",
    },
    {
      label: "Owners",
      content: <AvatarStack items={owners} />,
    },
    {
      label: "Last Updated",
      content: formatDistanceToNowStrict(new Date(project.updated_at), { addSuffix: true }),
    },
    {
      label: "Datasets",
      content: attachedDatasets?.length ?? "—",
    },
    { label: "Agents", content: attachedAgents?.length ?? "—" },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        <EntityOverviewHeader
          tiles={tiles}
          icon={<Notebook className="size-5" />}
          title={
            <EditableTitle
              value={project.name}
              canEdit={isOwner}
              label="project name"
              onSave={handleRename}
            />
          }
        >
          <EditableDescription
            value={project.description}
            canEdit={isOwner}
            onSave={handleSaveDescription}
          />
        </EntityOverviewHeader>
      </div>
    </div>
  );
};
